const ALLOWED_EXTENSIONS = new Set([
    'jpg',
    'jpeg',
    'png',
    'gif',
    'webp',
    'pdf',
    'txt',
    'doc',
    'docx',
]);

const IMAGE_EXTENSIONS = new Set([
    'jpg',
    'jpeg',
    'png',
    'gif',
    'webp',
]);

function fileExtension(file) {
    const parts = file.name.toLowerCase().split('.');

    return parts.length > 1
        ? parts.pop()
        : '';
}

function fileKey(file) {
    return [
        file.name,
        file.size,
        file.lastModified,
    ].join(':');
}

function formatFileSize(bytes) {
    if (bytes < 1024) {
        return `${bytes} B`;
    }

    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} KB`;
    }

    return `${(
        bytes
        / 1024
        / 1024
    ).toFixed(1)} MB`;
}

function createImagePreview(file) {
    const image = document.createElement('img');

    image.src = URL.createObjectURL(file);
    image.alt = file.name;
    image.className =
        'h-16 w-16 rounded-xl object-cover';

    image.addEventListener(
        'load',
        () => URL.revokeObjectURL(image.src),
        { once: true },
    );

    return image;
}

function createFileIcon() {
    const icon = document.createElement('div');

    icon.className =
        'flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-white/10 text-2xl';

    icon.textContent = '📎';

    return icon;
}

const initializedAttachmentQueues =
    new WeakSet();

export const initializeAttachmentQueue = (
    container
) => {
    if (
        initializedAttachmentQueues.has(
            container
        )
    ) {
        return;
    }

    initializedAttachmentQueues.add(
        container
    );
    const input = container.querySelector(
        '[data-message-attachment-input]',
    );

    const dropzone = container.querySelector(
        '[data-message-attachment-dropzone]',
    );

    const preview = container.querySelector(
        '[data-message-attachment-preview]',
    );

    const error = container.querySelector(
        '[data-message-attachment-error]',
    );

    const count = container.querySelector(
        '[data-message-attachment-count]',
    );

    if (
        ! input
        || ! dropzone
        || ! preview
        || ! error
        || ! count
    ) {
        return;
    }

    const maximumFiles = Number(
        container.dataset.maximumFiles ?? 5,
    );

    const maximumFileSize = Number(
        container.dataset.maximumFileSize
        ?? 10485760,
    );

    let queuedFiles = [];
		
		container.resetAttachmentQueue = () => {
    queuedFiles = [];

    input.value = '';

    clearError();

    syncInputFiles();

    renderQueue();
};

    const showError = (message) => {
        error.textContent = message;
        error.classList.remove('hidden');
    };

    const clearError = () => {
        error.textContent = '';
        error.classList.add('hidden');
    };

    const syncInputFiles = () => {
        const transfer = new DataTransfer();

        queuedFiles.forEach((file) => {
            transfer.items.add(file);
        });

        input.files = transfer.files;
    };

    const renderQueue = () => {
        preview.replaceChildren();

        if (queuedFiles.length === 0) {
            preview.classList.add('hidden');
            count.classList.add('hidden');
            count.textContent = '';

            return;
        }

        preview.classList.remove('hidden');
        count.classList.remove('hidden');

        count.textContent =
            `${queuedFiles.length} of `
            + `${maximumFiles} files selected`;

        queuedFiles.forEach((file) => {
            const extension = fileExtension(file);

            const item = document.createElement('div');

            item.className =
                'flex items-center gap-4 rounded-2xl '
                + 'border border-white/10 bg-slate-900 '
                + 'p-3';

            const visual = IMAGE_EXTENSIONS.has(extension)
                ? createImagePreview(file)
                : createFileIcon();

            const details = document.createElement('div');

            details.className = 'min-w-0 flex-1';

            const name = document.createElement('p');

            name.className =
                'truncate text-sm font-bold text-white';

            name.textContent = file.name;

            const size = document.createElement('p');

            size.className =
                'mt-1 text-xs text-slate-400';

            size.textContent = formatFileSize(file.size);

            details.append(name, size);

            const removeButton =
                document.createElement('button');

            removeButton.type = 'button';
            removeButton.className =
                'flex h-9 w-9 shrink-0 items-center '
                + 'justify-center rounded-full border '
                + 'border-red-400/30 bg-red-400/10 '
                + 'font-black text-red-200 '
                + 'hover:bg-red-400/20';

            removeButton.textContent = '×';
            removeButton.setAttribute(
                'aria-label',
                `Remove ${file.name}`,
            );

            removeButton.addEventListener(
                'click',
                () => {
                    const key = fileKey(file);

                    queuedFiles = queuedFiles.filter(
                        (queuedFile) =>
                            fileKey(queuedFile) !== key,
                    );

                    clearError();
                    syncInputFiles();
                    renderQueue();
                },
            );

            item.append(
                visual,
                details,
                removeButton,
            );

            preview.append(item);
        });
    };

    const addFiles = (files) => {
        clearError();

        const existingKeys = new Set(
            queuedFiles.map(fileKey),
        );

        for (const file of files) {
            const extension = fileExtension(file);

            if (! ALLOWED_EXTENSIONS.has(extension)) {
                showError(
                    `${file.name} is not a supported file type.`,
                );

                continue;
            }

            if (file.size > maximumFileSize) {
                showError(
                    `${file.name} is larger than 10 MB.`,
                );

                continue;
            }

            const key = fileKey(file);

            if (existingKeys.has(key)) {
                showError(
                    `${file.name} is already selected.`,
                );

                continue;
            }

            if (queuedFiles.length >= maximumFiles) {
                showError(
                    `You can attach a maximum of `
                    + `${maximumFiles} files.`,
                );

                break;
            }

            queuedFiles.push(file);
            existingKeys.add(key);
        }

        syncInputFiles();
        renderQueue();
    };

    input.addEventListener('change', () => {
        addFiles(Array.from(input.files ?? []));
    });

    const preventDefaults = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    [
        'dragenter',
        'dragover',
        'dragleave',
        'drop',
    ].forEach((eventName) => {
        dropzone.addEventListener(
            eventName,
            preventDefaults,
        );
    });

    [
        'dragenter',
        'dragover',
    ].forEach((eventName) => {
        dropzone.addEventListener(
            eventName,
            () => {
                dropzone.classList.add(
                    'border-cyan-400',
                    'bg-cyan-400/10',
                );
            },
        );
    });

    [
        'dragleave',
        'drop',
    ].forEach((eventName) => {
        dropzone.addEventListener(
            eventName,
            () => {
                dropzone.classList.remove(
                    'border-cyan-400',
                    'bg-cyan-400/10',
                );
            },
        );
    });

    dropzone.addEventListener('drop', (event) => {
        const files = Array.from(
            event.dataTransfer?.files ?? [],
        );

        addFiles(files);
    });

    const form = input.closest('form');

    form?.addEventListener('submit', () => {
        syncInputFiles();
    });
}

export const initializeAttachmentQueues = (
    root = document
) => {
    root
        .querySelectorAll(
            '[data-message-attachment-queue]'
        )
        .forEach(
            initializeAttachmentQueue
        );
};

if (
    document.readyState
    === 'loading'
) {
    document.addEventListener(
        'DOMContentLoaded',
        initializeAttachmentQueues
    );
} else {
    initializeAttachmentQueues();
}